import { BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { useAuth } from '../../../hooks/useAuth';
import { useEffect, useState, useRef } from 'react';
import { IHistoricalPoints } from '../../../interfaces/IHistoricalPoints';
import './style.module.css';

export default function PointsChart(historicalPoints: IHistoricalPoints) {
  const { client } = useAuth();
  const [data, setData] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [animatedTotalPoints, setAnimatedTotalPoints] = useState(0); // Estado para o valor animado
  const chartRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    let pointsOrigin = [];

    if (historicalPoints?.pointsOrigin) {
      const dadosCompletos = transformarPontosParaArray(
        historicalPoints.pointsOrigin
      );
      pointsOrigin = dadosCompletos.filter((item) => item.pontos > 0);
    }
    setData(pointsOrigin);
  }, [historicalPoints]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          startCircleAnimation();
          startNumberAnimation(); // Inicia a animação do número
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
      }
    );

    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => {
      if (chartRef.current) {
        observer.unobserve(chartRef.current);
      }
      cancelAnimationFrame(animationRef.current);
    };
  }, [historicalPoints?.totalPoints]); // Dependência para reagir a mudanças no totalPoints

  const startCircleAnimation = () => {
    const duration = 1500;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const newProgress = Math.min(elapsedTime / duration, 1);
      setProgress(newProgress);

      if (newProgress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  const startNumberAnimation = () => {
    const duration = 1500; // Mesma duração que a animação do círculo
    const endValue = historicalPoints?.totalPoints || 0;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const currentValue = Math.floor(progress * endValue);
      setAnimatedTotalPoints(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  function transformarPontosParaArray(points) {
    const resultado = [];
    const ordemDesejada = [
      'webhookBrokerPoints',
      'advancedStepLead',
      'quiz',
      'researchAward',
      'salesConversion',
      'extraPoints',
      'leadRegistration',
      'membergetmember',
    ];

    const objetoOrdenado = {};
    ordemDesejada.forEach((chave) => {
      if (Object.prototype.hasOwnProperty.call(points, chave)) {
        objetoOrdenado[chave] = points[chave];
      }
    });

    for (const [caminho, valor] of Object.entries(objetoOrdenado)) {
      if (caminho === 'webhookBrokerPoints') {
        client?.brokerSteps.map((step, index) =>
          resultado.push({
            nome: `${step.name ? step.name : `Broker ${index}`}`,
            pontos: valor[index + 1] ? valor[index + 1] : 0,
          })
        );
      }
      if (caminho === 'advancedStepLead') {
        client?.steps.map((step, index) =>
          resultado.push({
            nome: `${step.name ? step.name : `Webhook ${index}`}`,
            pontos: valor[index + 1] ? valor[index + 1] : 0,
          })
        );
      }
      if (caminho === 'quiz') resultado.push({ nome: 'Quiz', pontos: valor });
      if (caminho === 'researchAward')
        resultado.push({ nome: 'Pesquisas', pontos: valor });
      if (caminho === 'salesConversion')
        resultado.push({ nome: 'Conversão', pontos: valor });
      if (caminho === 'extraPoints')
        resultado.push({ nome: 'Pontos extras', pontos: valor });
      if (caminho === 'leadRegistration')
        resultado.push({ nome: 'Leads', pontos: valor });
      if (caminho === 'membergetmember')
        resultado.push({ nome: 'Seus convidados', pontos: valor });
    }

    return resultado;
  }

  return (
    <div
      ref={chartRef}
      className="grafico-container"
      style={{
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <p className="fw-semibold">Pontos totais</p>

      <div
        style={{
          minWidth: '100%',
          height: '200px',
          display: 'flex',
          alignItems: 'center',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'auto',
          scrollbarColor: '#888 transparent',
        }}
      >
        <div
          style={{
            width: '112px',
            height: '112px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '20px',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <svg width="112" height="112" viewBox="0 0 112 112">
            <circle
              cx="56"
              cy="56"
              r="50"
              fill="none"
              stroke="#e0e0e0"
              strokeWidth="10"
            />
            <circle
              cx="56"
              cy="56"
              r="50"
              fill="none"
              stroke={client?.primaryColor || '#3f51b5'}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray="314"
              strokeDashoffset={314 - progress * 314}
              transform="rotate(-90 56 56)"
              style={{
                transition: 'stroke-dashoffset 1.5s ease-out',
                opacity: isVisible ? 1 : 0,
              }}
            />
          </svg>
          <div
            style={{
              position: 'absolute',
              fontSize: '22px',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            {animatedTotalPoints.toLocaleString()} {/* Exibe o valor animado */}
          </div>
        </div>

        <div
          style={{
            minWidth: '700px',
            height: '200px',
            flexShrink: 0,
          }}
        >
          <BarChart
            width={data.length * 100}
            height={200}
            data={data}
            margin={{ top: 5, right: 30, left: 30, bottom: 40 }}
          >
            <XAxis
              dataKey="nome"
              tickLine={false}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
              tickFormatter={(value) =>
                value.length > 10 ? `${value.substring(0, 10)}...` : value
              }
            />
            <YAxis hide />
            <Tooltip />
            <Bar
              dataKey="pontos"
              fill={`${client?.primaryColor}`}
              radius={[5, 5, 0, 0]}
              barSize={30}
              animationBegin={isVisible ? 200 : 0}
              animationDuration={isVisible ? 2000 : 0}
              animationEasing="ease-in"
              isAnimationActive={isVisible}
            />
          </BarChart>
        </div>
      </div>
    </div>
  );
}
